import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Agent, EnrichedApproval } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { useApp } from '../contexts/AppContext';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { MarkdownPreview } from '../components/ui/MarkdownPreview';

function timeAgo(iso: string | Date): string {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function ApprovalsPage() {
  const { workspaceId } = useApp();
  const [approvals, setApprovals] = useState<EnrichedApproval[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [tab, setTab] = useState<'pending' | 'history'>('pending');
  const [reasons, setReasons] = useState<Record<string, string>>({});

  function load(next = tab) {
    const status = next === 'history' ? 'decided' : 'pending';
    void apiClient.listApprovalsPage({ status }).then((page) => setApprovals(page.items)).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : 'Failed to load approvals');
    });
  }

  useEffect(() => {
    void apiClient.listAgents().then((page) => setAgents(page.items)).catch(() => setAgents([]));
  }, []);

  useEffect(() => {
    load();
  }, [workspaceId, tab]);

  const groups = useMemo(() => {
    const byRun = new Map<string, { runId: string; run: EnrichedApproval['run']; approvals: EnrichedApproval[]; conversationId?: string }>();
    for (const approval of approvals) {
      const existing = byRun.get(approval.runId);
      if (existing) {
        existing.approvals.push(approval);
        existing.conversationId ??= approval.conversationId;
      } else {
        byRun.set(approval.runId, { runId: approval.runId, run: approval.run, approvals: [approval], conversationId: approval.conversationId });
      }
    }
    return [...byRun.values()].sort((a, b) => new Date(b.run.createdAt).getTime() - new Date(a.run.createdAt).getTime());
  }, [approvals]);

  async function decide(approval: EnrichedApproval, approve: boolean) {
    setError('');
    setSuccess('');
    setDecidingId(approval.id);
    try {
      await apiClient.decideApproval(approval.id, approve, reasons[approval.id]);
      setReasons((current) => ({ ...current, [approval.id]: '' }));
      setSuccess(approve ? 'Approved. The run is resuming.' : 'Rejected. The run is resuming.');
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit decision');
    } finally {
      setDecidingId(null);
    }
  }

  function agentName(agentId: string): string {
    if (!agentId) return 'Unknown agent';
    return agents.find((agent) => agent.id === agentId)?.name ?? agentId.slice(0, 8);
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Approvals</h1>
          <p className="text-sm text-gray-500">Review and decide on sensitive tool calls before they execute.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
            <button type="button" className={`px-3 py-1 text-sm ${tab === 'pending' ? 'bg-blue-600 text-white' : ''}`} onClick={() => setTab('pending')}>Pending</button>
            <button type="button" className={`px-3 py-1 text-sm ${tab === 'history' ? 'bg-blue-600 text-white' : ''}`} onClick={() => setTab('history')}>History</button>
          </div>
          {approvals.length > 0 && <span className="text-xs text-gray-400">{approvals.length} {tab === 'pending' ? 'pending' : 'decided'} across {groups.length} run(s)</span>}
        </div>
      </div>
      {error && <div className="mt-4 rounded-lg border border-red-300 bg-red-50 p-2 text-sm text-red-700">{error}</div>}
      {success && <div className="mt-4 rounded-lg border border-green-300 bg-green-50 p-2 text-sm text-green-700">{success}</div>}
      {approvals.length === 0 && !error && (
        <div className="mt-4 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <div className="font-medium">{tab === 'pending' ? 'No pending approvals' : 'No approval history'}</div>
          <div className="text-sm text-gray-500">{tab === 'pending' ? 'All tool calls are either safe or already decided.' : 'Decided approvals will appear here.'}</div>
        </div>
      )}
      <div className="mt-4 flex flex-col gap-6">
        {groups.map((group) => (
          <div key={group.runId}>
            <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
              <span className="text-sm font-semibold">{agentName(group.run.agentId)}</span>
              <Badge variant="secondary">{group.run.status}</Badge>
              <span className="text-xs text-gray-400">· {timeAgo(group.run.createdAt)}</span>
              <span className="min-w-0 flex-1 truncate text-xs text-gray-500">{group.run.input || 'No request text recorded'}</span>
              <Badge variant="outline">{group.approvals.length} {tab === 'pending' ? 'pending' : 'decided'}</Badge>
              {group.conversationId && <Link className="text-xs text-blue-600" to={`/collaborate?conversationId=${group.conversationId}`}>View in session</Link>}
              <Link className="text-xs text-blue-600" to={`/runs?runId=${group.runId}`}>View run</Link>
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(380px,1fr))] gap-4">
              {group.approvals.map((approval) => (
                <div key={approval.id} className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                  <div className="text-sm font-semibold">{approval.toolKey}</div>
                  <div className="mt-2 max-h-40 overflow-auto text-xs">
                    <MarkdownPreview content={'```json\n' + JSON.stringify(approval.toolInput ?? {}, null, 2) + '\n```'} />
                  </div>
                  {tab === 'history' ? (
                    <div className="mt-3 rounded-lg border border-gray-200 p-3 text-xs dark:border-gray-700">
                      <div>Decision: {approval.status}</div>
                      {approval.decidedBy && <div>Actor: {approval.decidedBy}</div>}
                      {approval.reason && <div>Reason: {approval.reason}</div>}
                    </div>
                  ) : (
                    <>
                      <textarea className="mt-3 w-full rounded border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-800" rows={2} placeholder="Add a note about your decision..." value={reasons[approval.id] ?? ''} onChange={(event) => setReasons((current) => ({ ...current, [approval.id]: event.target.value }))} />
                      <div className="mt-2 flex gap-3">
                        <Button className="flex-1 bg-green-600 hover:bg-green-700" size="sm" disabled={decidingId === approval.id} onClick={() => void decide(approval, true)}>Approve</Button>
                        <Button className="flex-1" variant="destructive" size="sm" disabled={decidingId === approval.id} onClick={() => void decide(approval, false)}>Reject</Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
